import { useAuth } from '@pooflabs/web';
import { ArrowLeft, Loader2, Share2, ShieldCheck, Sparkles } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AgentAvatar } from '@/components/AgentAvatar';
import { AgentRentalDialog } from '@/components/AgentRentalDialog';
import TopNav from '@/components/TopNav';
import { RetroGrid } from '@/components/effects';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeAgents, type AgentsResponse } from '@/lib/collections/agents';
import { formatRating, formatSol, lamportsToSol, parseCapabilityTags } from '@/utils/format';
import { getAgentHelperText, getWhatYouGet, isFunctionalAgentId } from '@/utils/agentContent';
import { getOrCreateSessionId } from '@/utils/hireAgent';
import { shareAgentLink } from '@/utils/shareAgent';

export const AgentProfilePage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { user, login } = useAuth();

  // Public, read-only profile — no auth gate here so shared links work for
  // logged-out visitors. Only the Hire/chat action requires a wallet.
  const { data: agent, loading } = useRealtimeData<AgentsResponse | null>(
    subscribeAgents,
    !!agentId,
    agentId ?? ''
  );

  const functional = !!agent && (isFunctionalAgentId(agentId) || agent.isFunctional);

  // Set when a logged-out visitor taps "Hire" — once `user` becomes truthy
  // (login resolves), the effect below opens the payment-choice dialog.
  const [pendingHire, setPendingHire] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [rentalDialogOpen, setRentalDialogOpen] = useState(false);

  useEffect(() => {
    if (pendingHire && user && agentId) {
      setPendingHire(false);
      setRentalDialogOpen(true);
    }
  }, [pendingHire, user, agentId]);

  const goToChat = () => {
    if (!agentId || !user) return;
    const sessionId = getOrCreateSessionId(agentId, user.address);
    navigate(`/agents/${agentId}/chat?session=${sessionId}`);
  };

  const handleCta = async () => {
    if (!agent || !agentId) return;
    if (!functional) {
      toast.info('This agent is coming soon');
      return;
    }
    if (!user) {
      setPendingHire(true);
      setLoggingIn(true);
      try {
        await login();
      } catch {
        toast.error('Login failed');
        setPendingHire(false);
      } finally {
        setLoggingIn(false);
      }
      return;
    }
    setRentalDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#0b0b14] text-white">
      <TopNav variant="dark" />

      <div className="relative overflow-hidden">
        <RetroGrid className="opacity-30" angle={55} cellSize={46} color="rgba(139,92,246,0.35)" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0b0b14]/70 to-[#0b0b14]" />

        <div className="relative z-10 container max-w-lg mx-auto pt-10 sm:pt-14 pb-24 lg:pb-14 px-4">
          <button
            onClick={() => navigate('/agents')}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/70 hover:text-white bg-white/[0.06] hover:bg-white/10 border border-white/10 rounded-full px-4 py-2 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Marketplace
          </button>

          {loading && <div className="py-24 text-center text-white/60">Loading agent…</div>}

          {!loading && !agent && <div className="py-24 text-center text-white/60">Agent not found.</div>}

          {!loading && agent && agentId && (
            <ProfileCard
              agent={agent}
              agentId={agentId}
              functional={functional}
              onCta={handleCta}
              ctaLoading={loggingIn}
            />
          )}

          {agent && agentId && user && (
            <AgentRentalDialog
              agent={agent}
              agentId={agentId}
              walletAddress={user.address}
              open={rentalDialogOpen}
              onOpenChange={setRentalDialogOpen}
              onPayPerUse={goToChat}
              onRented={goToChat}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const ProfileCard: React.FC<{
  agent: AgentsResponse;
  agentId: string;
  functional: boolean;
  onCta: () => void;
  ctaLoading?: boolean;
}> = ({ agent, agentId, functional, onCta, ctaLoading }) => {
  const tags = parseCapabilityTags(agent.capabilityTags);
  const bullets = getWhatYouGet({ id: agentId, name: agent.name, capabilityTags: agent.capabilityTags, isFunctional: agent.isFunctional });
  const helperText = getAgentHelperText(agentId);

  return (
    <div className="relative rounded-3xl border border-white/10 bg-white/[0.06] backdrop-blur-2xl shadow-2xl p-6 sm:p-8 animate-fade-in-up">
      <button
        onClick={() => shareAgentLink(agentId, agent.name)}
        aria-label="Share this agent"
        className="absolute top-5 right-5 sm:top-7 sm:right-7 p-2.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white/80 hover:text-white transition-colors z-10"
      >
        <Share2 className="w-4 h-4" />
      </button>

      <div className="flex flex-col items-center text-center">
        <AgentAvatar
          avatarUrl={agent.avatarUrl}
          avatarGradient={agent.avatarGradient}
          name={agent.name}
          size="xl"
          square
          className="shadow-xl shadow-black/40"
        />
        <div className="flex items-center gap-1.5 mt-4">
          <h1 className="text-2xl font-extrabold">{agent.name}</h1>
          {functional && <ShieldCheck className="w-5 h-5 text-[#00C805]" aria-label="Verified functional agent" />}
        </div>
        <p className="text-white/60 max-w-md mt-2 text-sm leading-relaxed">{agent.description}</p>
      </div>

      {/* Tags */}
      {tags.length > 0 && (
        <div className="flex flex-wrap justify-center gap-2 mt-5">
          {tags.map((tag) => (
            <span key={tag} className="text-xs font-semibold bg-white/10 text-white/80 px-3 py-1.5 rounded-full border border-white/10">
              [{tag}]
            </span>
          ))}
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3 mt-7">
        <ProfileStat label="Jobs" value={agent.statsRuns.toLocaleString('en-US')} />
        <ProfileStat label="Rating" value={`${formatRating(agent.statsRating)}★`} />
        <ProfileStat label="Volume" value={`${lamportsToSol(agent.statsVolume).toFixed(2)} SOL`} />
      </div>

      <p className="text-center text-sm text-white/60 mt-4">
        Price per Request: <span className="font-bold text-white">{formatSol(agent.pricePerCall)}</span>
      </p>

      {/* What you get */}
      <div className="mt-6 rounded-2xl bg-white/[0.04] border border-white/10 p-5">
        <h2 className="font-bold flex items-center gap-1.5 mb-3 text-sm">
          <Sparkles className="w-4 h-4 text-[#00C805]" />
          What you get
        </h2>
        <ul className="space-y-2">
          {bullets.map((bullet, i) => (
            <li key={i} className="text-sm text-white/70 flex gap-2">
              <ShieldCheck className="w-4 h-4 text-[#00C805] shrink-0 mt-0.5" />
              {bullet}
            </li>
          ))}
        </ul>
      </div>

      {/* CTA */}
      <button
        onClick={onCta}
        disabled={ctaLoading}
        className={`w-full rounded-full font-bold py-4 mt-7 shadow-lg transition-all duration-300 flex items-center justify-center gap-2 ${
          functional
            ? 'bg-[#00C805] text-white hover:bg-[#00b004] shadow-[#00C805]/30 disabled:opacity-70'
            : 'bg-white/10 text-white/50 cursor-default'
        }`}
      >
        {ctaLoading && <Loader2 className="w-4 h-4 animate-spin" />}
        {ctaLoading
          ? 'Connecting wallet…'
          : functional
            ? `Hire Agent — ${formatSol(agent.pricePerCall)}`
            : 'Coming soon'}
      </button>
      {functional && helperText && <p className="text-center text-xs text-white/50 mt-2.5">{helperText}</p>}
    </div>
  );
};

const ProfileStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-xl bg-white/[0.04] border border-white/10 py-3 text-center">
    <p className="font-extrabold text-lg">{value}</p>
    <p className="text-[11px] text-white/50 uppercase tracking-wide">{label}</p>
  </div>
);

export default AgentProfilePage;
